import { useState, useEffect } from "react";
import { persistor } from "../store/store";

/**
 * True once redux-persist has finished rehydrating every persisted slice.
 *
 * The persistor is the source rather than a `_persist` marker in the state
 * tree. That marker belongs to whichever reducer `persistReducer` wraps, and
 * persistence here is per-slice, so no marker sits at the root. `bootstrapped`
 * covers every nested persistor at once: each registers itself on PERSIST, and
 * the flag flips when the last one has rehydrated.
 *
 * Components under `PersistGate` mount after that point, so this is usually
 * true on the first render. It still subscribes, for any caller outside the
 * gate.
 *
 * @returns {boolean} Whether rehydration has completed.
 */
export const useReduxPersistRehydrated = () => {
  const [isRehydrated, setIsRehydrated] = useState(() => persistor.getState().bootstrapped);

  useEffect(() => {
    if (isRehydrated) return undefined;

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        setIsRehydrated(true);
        unsubscribe();
      }
    });

    return unsubscribe;
  }, [isRehydrated]);

  return isRehydrated;
};
